import React, { useEffect } from 'react';

interface NavItem {
    value: string;
    label: string;
    disabled?: boolean;
}

interface NavListProps {
    items: NavItem[];
    activeValue?: string;
    onChange: (value: string) => void;
    className?: string;
    variant?: 'tabs' | 'pills' | 'underline';
    size?: 'sm' | 'md' | 'lg';
    orientation?: 'horizontal' | 'vertical';
}

const NavList: React.FC<NavListProps> = ({
    items,
    activeValue,
    onChange,
    className = '',
    variant = 'underline',
    size = 'md',
    orientation = 'horizontal',
}) => {
    const handleItemClick = (item: NavItem) => {
        if (!item.disabled) {
            onChange(item.value);
        }
    };

    const getBaseClasses = () => {
        const orientationClasses =
            orientation === 'horizontal'
                ? 'flex flex-row overflow-x-scroll nav-horizontal'
                : 'flex flex-col nav-vertical';

        return `${orientationClasses} ${className}`;
    };

    const getItemClasses = (item: NavItem, isActive: boolean) => {
        const sizeClasses = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-4 py-2 text-base',
            lg: 'px-6 py-3 text-lg',
        };

        const baseClasses = `
            ${sizeClasses[size]}
            cursor-pointer
            transition-all
            duration-200
            whitespace-nowrap
            font-medium
        `;

        const disabledClasses = item.disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:opacity-80';

        let variantClasses = '';

        switch (variant) {
            case 'tabs':
                variantClasses = isActive
                    ? 'bg-blue-500 text-white rounded-t-lg border-b-2 border-blue-500'
                    : 'bg-gray-100 text-gray-700 rounded-t-lg border-b-2 border-transparent hover:bg-gray-200';
                break;

            case 'pills':
                variantClasses = isActive
                    ? 'bg-blue-500 text-white rounded-full'
                    : 'bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200';
                break;

            case 'underline':
            default:
                variantClasses = isActive
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 border-b-2 border-transparent hover:text-gray-800 hover:border-gray-300';
                break;
        }

        return `${baseClasses} ${variantClasses} ${disabledClasses}`.trim();
    };

    const getContainerClasses = () => {
        if (variant === 'tabs') {
            return 'border-b border-gray-200';
        }
        if (variant === 'underline') {
            return 'border-b border-gray-200';
        }
        return '';
    };

    useEffect(() => {
        if (!activeValue || activeValue == 'all') {
            activeValue = items[0]?.value;
        }
    }, [activeValue]);
    return (
        <nav className={`${getBaseClasses()} ${getContainerClasses()}`}>
            <div className="w-full overflow-x-scroll flex sm:max-w-screen-sm lg:max-w-screen-lg md:max-w-screen-md xl:max-w-screen-xl custom-scrollbar pl-10">
                {items.map((item) => {
                    const isActive = activeValue === item.value;

                    return (
                        <button
                            key={item.value}
                            onClick={() => handleItemClick(item)}
                            disabled={item.disabled}
                            className={getItemClasses(item, isActive)}
                            aria-current={isActive ? 'page' : undefined}
                            role="tab"
                            aria-selected={isActive}
                        >
                            {item.label.replace(/([a-zA-Z])(\d)/, '$1 $2')}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

export default React.memo(NavList);
